import { MagnifyingGlassIcon } from "@phosphor-icons/react";
import { type SubmitEvent, useState } from "react";

/** The API's limit on a catalog search (``q``). */
const MAX_QUERY_LENGTH = 200;

interface SourceSearchBarProps {
  /** The search in effect; the box starts from it. */
  query: string | undefined;
  /** Run a search: trimmed, and empty to clear it. */
  onSearch: (query: string) => void;
}

/**
 * The Source title bar: heading, search box and Search button. Typing only
 * edits a draft; Enter or the button runs it, so each search embeds once.
 */
export function SourceSearchBar({ query, onSearch }: SourceSearchBarProps) {
  const [draft, setDraft] = useState(query ?? "");

  const submit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSearch(draft.trim());
  };

  return (
    <div className="flex h-11 w-full items-center gap-4">
      <h2 className="text-heading-modal w-[280px] shrink-0">
        What are you looking for?
      </h2>
      <search className="h-full min-w-0 flex-1">
        <form onSubmit={submit} className="flex h-full items-center gap-4">
          <label className="flex h-full min-w-0 flex-1 items-center gap-2 rounded-lg border border-[rgba(79,94,110,0.7)] bg-[rgba(79,94,110,0.7)] py-2 pr-2 pl-3">
            <MagnifyingGlassIcon
              size={16}
              aria-hidden
              className="shrink-0 text-[rgba(240,240,240,0.6)]"
            />
            <input
              type="search"
              aria-label="Search the catalog"
              value={draft}
              maxLength={MAX_QUERY_LENGTH}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="Search products, colors, materials..."
              className="min-w-0 flex-1 bg-transparent text-sm text-[var(--text-primary)] outline-none placeholder:text-[rgba(240,240,240,0.6)]"
            />
          </label>
          <button
            type="submit"
            className="inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-lg bg-[var(--btn-modal-primary)] px-4 text-sm font-semibold text-white hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--btn-modal-primary)] focus-visible:ring-offset-2"
          >
            Search
            <MagnifyingGlassIcon size={16} aria-hidden />
          </button>
        </form>
      </search>
    </div>
  );
}
